import { createStore } from 'vuex'
import DcbService from '@/services/dcbService'
import { transformUserApiResponse, DEFAULT_USER_STATS, getSubscriptionFromGetUser, ServiceIds } from '@/config'

export default createStore({
  state: {
    user: null,
    msisdn: localStorage.getItem('msisdn') || undefined,
    sessionId: localStorage.getItem('sessionId') || undefined,
    serviceId: undefined,
    brandConfig: undefined,
    isLoading: false,
    showLoginModal: false,
    showOtpModal: false,
    showOffnetModal: false,
    showSubscribeModal: false,
    showUnsubscribeModal: false,
    showSubscribeSuccessModal: false,
    showSubscribeErrorModal: false,
    subscribeErrorMessage: '',
    subscribeErrorReason: '',
    otpMessage: '',
    pendingAction: null,
    intendedPath: null,
    error: null,
    feedArticles: [],
    campaignId: 0,
    defferedPrompt: null,
    showAddToHomeScreen: false,
    _userFetchPromise: null
  },

  getters: {
    isAuthenticated: (state) => !!state.sessionId,

    isSubscribed: (state) => {
      return state.user?.status === 'Active' || state.user?.isSubscribed || false
    },

    userType: (_, getters) => {
      if (!getters.isAuthenticated) return 'guest'
      return getters.isSubscribed ? 'premium' : 'free'
    },

    username: (state) => state.user?.username || 'Guest',

    userStats: (state) => state.user?.stats || DEFAULT_USER_STATS,

    userTeams: (state) => state.user?.teams || [],

    questionLimit: (_, getters) => getters.isSubscribed ? 10 : 3
  },

  mutations: {
    setUser(state, user) {
      state.user = user
    },
    setUsername(state, username) {
      if (state.user) {
        state.user = { ...state.user, username: username.trim() || state.user.username }
      }
    },
    setUserTeams(state, teams) {
      if (state.user) {
        state.user = { ...state.user, teams: Array.isArray(teams) ? teams : [] }
      }
    },
    setUserAvatar(state, avatar) {
      if (state.user) {
        state.user = { ...state.user, avatar: String(avatar || '') }
      }
    },
    setMsisdn(state, msisdn) {
      state.msisdn = msisdn
      if (msisdn) {
        localStorage.setItem('msisdn', msisdn)
      } else {
        localStorage.removeItem('msisdn')
      }
    },
    setSessionId(state, sessionId) {
      state.sessionId = sessionId
      if (sessionId) {
        localStorage.setItem('sessionId', sessionId)
      } else {
        localStorage.removeItem('sessionId')
      }
    },
    setServiceId(state, serviceId) {
      state.serviceId = serviceId
    },
    setIsSubscribed(state, isSubscribed) {
      const value = isSubscribed === true || isSubscribed === 'true' || isSubscribed === 1
      localStorage.setItem('isSubscribed', value.toString())
    },
    setBrandConfig(state, brandConfig) {
      state.brandConfig = Object.freeze(brandConfig)
    },
    setIsLoading(state, isLoading) {
      state.isLoading = isLoading
    },
    setError(state, error) {
      state.error = error
    },
    setShowLoginModal(state, show) {
      state.showLoginModal = show
    },
    setShowOtpModal(state, show) {
      state.showOtpModal = show
    },
    setShowOffnetModal(state, show) {
      state.showOffnetModal = show
    },
    setShowSubscribeModal(state, show) {
      state.showSubscribeModal = show
    },
    setShowUnsubscribeModal(state, show) {
      state.showUnsubscribeModal = show
    },
    setShowSubscribeSuccessModal(state, show) {
      state.showSubscribeSuccessModal = show
    },
    setShowSubscribeErrorModal(state, show) {
      state.showSubscribeErrorModal = show
      if (!show) {
        state.subscribeErrorMessage = ''
        state.subscribeErrorReason = ''
      }
    },
    setSubscribeErrorMessage(state, message) {
      state.subscribeErrorMessage = message || ''
    },
    setSubscribeErrorReason(state, reason) {
      state.subscribeErrorReason = reason || ''
    },
    closeAllModals(state) {
      state.showLoginModal = false
      state.showOtpModal = false
      state.showOffnetModal = false
      state.showSubscribeModal = false
      state.showUnsubscribeModal = false
      state.showSubscribeSuccessModal = false
      state.showSubscribeErrorModal = false
      state.subscribeErrorMessage = ''
      state.subscribeErrorReason = ''
      state.error = null
    },
    setOtpMessage(state, message) {
      state.otpMessage = message
    },
    setPendingAction(state, action) {
      state.pendingAction = action
    },
    setIntendedPath(state, path) {
      state.intendedPath = path || null
    },
    setFeedArticles(state, articles) {
      state.feedArticles = articles
    },
    setCampaignId(state, campaignId) {
      state.campaignId = campaignId
    },
    setDefferedPrompt(state, prompt) {
      state.defferedPrompt = prompt
    },
    setShowAddToHomeScreen(state, show) {
      state.showAddToHomeScreen = show
    },
    _setUserFetchPromise(state, promise) {
      state._userFetchPromise = promise
    },
    _clearUserFetchPromise(state) {
      state._userFetchPromise = null
    }
  },

  actions: {
    async fetchAndSetUser({ commit, state }, { force = false } = {}) {
      if (!force && state.user) return state.user

      const msisdn = state.msisdn
      if (!msisdn) return null

      // Deduplicate: if a fetch is already in progress (and not forcing refresh), wait for it
      if (!force && state._userFetchPromise) {
        return state._userFetchPromise
      }

      const fetchPromise = (async () => {
        try {
          const result = await DcbService.getUserViaProductId(msisdn)
          const transformed = transformUserApiResponse(result, state.user?.stats)
          if (transformed) {
            commit('setUser', transformed)
            commit('setIsSubscribed', transformed.isSubscribed)
            commit('setServiceId', transformed.serviceId)
          }
          return transformed
        } finally {
          commit('_clearUserFetchPromise')
        }
      })()
      commit('_setUserFetchPromise', fetchPromise)
      return fetchPromise
    },

    async checkSession({ commit, state, dispatch }) {
      const msisdn = state.msisdn || localStorage.getItem('msisdn')
      const sessionId = state.sessionId || localStorage.getItem('sessionId')

      if (!sessionId || !msisdn) return false

      commit('setMsisdn', msisdn)
      commit('setSessionId', sessionId)

      const user = await dispatch('fetchAndSetUser', { force: false })
      return !!user
    },

    clearSession({ commit }) {
      commit('setUser', null)
      commit('setMsisdn', null)
      commit('setSessionId', null)
      commit('setIsSubscribed', false)
      commit('closeAllModals')
    },

    /**
     * Login using e-msisdn from URL params (e.g. from voucher store redirect).
     * Decrypts e-msisdn, fetches user, creates session, and persists auth.
     * @param {Object} _ctx - store context
     * @param {string} encryptedMsisdn - encrypted MSISDN from ?e-msisdn=...
     * @returns {{ success: boolean, error?: string }}
     */
    async loginWithEmsisdn({ commit, dispatch }, encryptedMsisdn) {
      if (!encryptedMsisdn || typeof encryptedMsisdn !== 'string') {
        return { success: false, error: 'Invalid e-msisdn' }
      }
      try {
        const decryptResult = await DcbService.decryptMsisdn(encryptedMsisdn.trim())
        if (!decryptResult.success || !decryptResult.rawMsisdn) {
          return { success: false, error: decryptResult.error || 'Decryption failed' }
        }
        const msisdn = DcbService.formatMsisdn(decryptResult.rawMsisdn)
        commit('setMsisdn', msisdn)

        const userResponse = await DcbService.getUserViaProductId(msisdn)
        const serviceId = userResponse?.success
          ? getSubscriptionFromGetUser(userResponse).serviceId
          : ServiceIds.free
        commit('setServiceId', serviceId)

        const sessionResult = await DcbService.createSessionId(msisdn, serviceId)
        if (sessionResult?.authError || !sessionResult?.sessionId) {
          return { success: false, error: 'Session creation failed' }
        }
        commit('setSessionId', sessionResult.sessionId)

        const user = await dispatch('fetchAndSetUser', { force: true })

        if (user) {
          // Tealium: fresh login via e-msisdn deep link (voucher-store redirect).
          import('@/services/tealium')
            .then((m) => m.postTealiumLogin())
            .catch(() => {})
        }

        return user ? { success: true } : { success: false, error: 'User fetch failed' }
      } catch (err) {
        console.error('e-msisdn login error:', err)
        await dispatch('clearSession')
        return { success: false, error: err?.message || 'Login failed' }
      }
    }
  }
})
